import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { ADS_ENTITY_LABELS_MAX_REFS, } from '@wit-support/admin-contracts';
import { EntityLabel } from '@wit-support/ui-domain';
import { adsApiUrl, getEntityLabels } from './api.js';
import { ADS_QUERY_DEFAULTS } from './query-policy.js';
const ID_RE = {
    page: /^\d{1,30}$/,
    instagram: /^\d{1,30}$/,
    pixel: /^\d{1,30}$/,
    audience: /^\d{1,30}$/,
    adaccount: /^act_\d{1,30}$/,
    post: /^\d{1,30}(_\d{1,30})?$/,
};
function normId(kind, id) {
    const t = id.trim();
    return kind === 'adaccount' && /^\d+$/.test(t) ? `act_${t}` : t;
}
/** `вид:id` или null — id не той формы (иначе admin-api отклонит весь запрос). */
export function entityRef(kind, id) {
    if (id == null)
        return null;
    const n = normId(kind, id);
    return ID_RE[kind].test(n) ? `${kind}:${n}` : null;
}
/** Ссылки без повторов и мусора, в стабильном порядке (ключ запроса). */
export function normalizeRefs(refs) {
    const out = new Set();
    for (const r of refs) {
        if (r == null)
            continue;
        const at = r.indexOf(':');
        if (at <= 0)
            continue;
        const kind = r.slice(0, at);
        if (!Object.hasOwn(ID_RE, kind))
            continue;
        const ref = entityRef(kind, r.slice(at + 1));
        if (ref != null)
            out.add(ref);
    }
    return [...out].sort();
}
/** Пачки по `ADS_ENTITY_LABELS_MAX_REFS`. */
export function chunkRefs(refs, size = ADS_ENTITY_LABELS_MAX_REFS) {
    const out = [];
    for (let i = 0; i < refs.length; i += size)
        out.push(refs.slice(i, i + size));
    return out;
}
/**
 * Ключ НЕ под `ADS_KEY`: мутации раздела сбрасывают `['ads']` целиком, а
 * имена от них не меняются — часовой кэш не должен пропадать после
 * каждого сохранения.
 */
export const ENTITY_LABELS_KEY = ['ads-entity-labels'];
export const entityLabelsKey = (pid, refs) => [...ENTITY_LABELS_KEY, pid, refs.join(',')];
const HOUR = 60 * 60_000;
/**
 * Названия сущностей экрана, сгруппированные по кабинету: ОДИН запрос на
 * кабинет (токен своего кабинета — пост чужой страницы не спрашивается
 * токеном первого). Пустой кабинет или пустой список — без запроса.
 */
export function useEntityLabelsByAccount(groups) {
    const norm = useMemo(() => [...groups.entries()]
        .filter(([pid]) => pid !== '')
        .map(([pid, refs]) => ({ pid, refs: normalizeRefs(refs) }))
        .filter((g) => g.refs.length > 0)
        .sort((x, y) => x.pid.localeCompare(y.pid)), [groups]);
    const results = useQueries({
        queries: norm.map(({ pid, refs }) => ({
            ...ADS_QUERY_DEFAULTS,
            queryKey: entityLabelsKey(pid, refs),
            queryFn: async () => {
                const parts = await Promise.all(chunkRefs(refs).map((c) => getEntityLabels(pid, c)));
                return parts.flatMap((p) => p.items);
            },
            staleTime: HOUR,
            gcTime: HOUR,
        })),
    });
    // пересобирается, только когда пришли новые данные (метка времени ответов)
    const stamp = results.map((r) => r.dataUpdatedAt).join(',');
    const byRef = useMemo(() => {
        const m = new Map();
        for (const r of results) {
            for (const l of r.data ?? [])
                m.set(`${l.kind}:${normId(l.kind, l.id)}`, l);
        }
        return m;
        // results — новый массив каждый рендер; данные меняются только вместе со stamp
    }, [stamp]);
    const labelOf = useCallback((kind, id) => id == null ? undefined : byRef.get(`${kind}:${normId(kind, id)}`), [byRef]);
    return {
        labelOf,
        isLoading: results.some((r) => r.isLoading),
        isError: results.some((r) => r.isError),
    };
}
/**
 * Названия сущностей экрана ОДНИМ запросом. `platformAccountId` — кабинет,
 * чьим токеном auto-ads спросит Meta при промахе справочников; null — не
 * спрашивать вовсе. Пустой список ссылок — запроса нет.
 */
export function useEntityLabels(platformAccountId, refs) {
    const groups = useMemo(() => new Map(platformAccountId ? [[platformAccountId, refs]] : []), [platformAccountId, refs]);
    return useEntityLabelsByAccount(groups);
}
/** Название из готового списка (`labels` строки кабинета) — без запроса. */
export function findLabel(labels, kind, id) {
    if (id == null || labels == null)
        return undefined;
    const n = normId(kind, id);
    return labels.find((l) => l.kind === kind && normId(kind, l.id) === n);
}
/**
 * `EntityLabel` по ответу `/labels`: `avatarUrl` — путь admin-api, сюда он
 * приходит абсолютным адресом; нет — инициалы, без запроса. `name` пропсом
 * главнее имени из ответа (своё имя экран знает лучше: имя кабинета).
 */
export function AdsEntity({ kind, id, label, name, username, ...rest }) {
    return (_jsx(EntityLabel, { kind: kind, id: label?.id ?? id, name: name ?? label?.name ?? null, username: username ?? label?.username ?? null, avatarSrc: label?.avatarUrl ? adsApiUrl(label.avatarUrl) : null, ...rest }));
}
//# sourceMappingURL=entity-labels.js.map